import React, { useState, useEffect } from 'react'
import { auth, db } from '../firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import {
  calculatePurchasePoints,
  getPesosPorPunto,
  getPuntosPorPeso,
  getTierForPoints,
  money,
  DEFAULT_TIERS,
} from '../utils/loyalty'

// ─── Cloud Functions ───────────────────────────────────────────────────────────
const functions        = getFunctions(undefined, 'us-central1')
const acreditarPuntosFn = httpsCallable(functions, 'acreditarPuntos')
const validarVoucherFn  = httpsCallable(functions, 'validarVoucher')
const utilizarVoucherFn = httpsCallable(functions, 'utilizarVoucher')

// ─── Mensajes de error legibles ────────────────────────────────────────────────
const MENSAJES = {
  USER_NOT_FOUND:        'No se encontró el cliente.',
  UNAUTHORIZED:          'No tenés permisos para esta operación.',
  INVALID_AMOUNT:        'El monto ingresado no es válido.',
  VOUCHER_NOT_FOUND:     'Voucher no encontrado.',
  VOUCHER_ALREADY_USED:  'Este voucher ya fue utilizado.',
  VOUCHER_EXPIRED:       'Este voucher está vencido.',
  VOUCHER_CANCELLED:     'Este voucher fue cancelado.',
  VOUCHER_INVALID_STATE: 'El voucher no está disponible.',
  OPERADOR_NOT_FOUND:    'Error de sesión del operador.',
}
function mensajeError(code) {
  return MENSAJES[code] || 'Ocurrió un error. Intentá de nuevo.'
}

const OperatorDashboard = ({ onLogout, localSeleccionado }) => {
  const [operador, setOperador]             = useState(null)
  const [config, setConfig]                 = useState(null)

  // Búsqueda de cliente
  const [dni, setDni]                       = useState('')
  const [clientInfo, setClientInfo]         = useState(null)

  // Acreditación
  const [purchaseAmount, setPurchaseAmount] = useState('')
  const [calculatedPoints, setCalculatedPoints] = useState(null)

  // Voucher
  const [voucherCode, setVoucherCode]       = useState('')
  const [voucherValidado, setVoucherValidado] = useState(null)  // datos del backend
  const [confirmarVoucher, setConfirmarVoucher] = useState(false) // pantalla de confirmación

  // UI
  const [isProcessing, setIsProcessing]     = useState(false)
  const [errorMessage, setErrorMessage]     = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // ─── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const snap = await getDoc(doc(db, 'configuracion_admin', 'global'))
        setConfig(snap.exists() ? snap.data() : { puntosPorPeso: 0.1, tiers: DEFAULT_TIERS })
      } catch {
        setConfig({ puntosPorPeso: 0.1, tiers: DEFAULT_TIERS })
      }
    }
    const fetchOperador = async () => {
      const user = auth.currentUser
      if (!user) return
      const snap = await getDoc(doc(db, 'usuarios', user.uid))
      if (snap.exists()) setOperador({ uid: user.uid, ...snap.data() })
    }
    fetchConfig()
    fetchOperador()
  }, [])

  // Bloquear Enter mientras se procesa
  useEffect(() => {
    const handler = (e) => { if (isProcessing && e.key === 'Enter') { e.preventDefault(); e.stopPropagation() } }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isProcessing])

  // Recalcular puntos cuando cambia el monto
  useEffect(() => {
    if (!clientInfo || !config || !purchaseAmount) { setCalculatedPoints(null); return }
    const amount = parseFloat(purchaseAmount)
    if (Number.isFinite(amount) && amount > 0) {
      setCalculatedPoints(calculatePurchasePoints(amount, config, clientInfo.multiplicador))
    } else {
      setCalculatedPoints(null)
    }
  }, [purchaseAmount, clientInfo, config])

  const resetMessages = () => { setErrorMessage(''); setSuccessMessage('') }

  // ─── Buscar cliente por DNI ──────────────────────────────────────────────────
  const handleSearchClient = async () => {
    setClientInfo(null)
    setCalculatedPoints(null)
    setVoucherValidado(null)
    setConfirmarVoucher(false)
    resetMessages()

    if (dni.length < 7 || dni.length > 9) {
      setErrorMessage('Ingresá un DNI válido (7-9 dígitos).')
      return
    }
    try {
      const snap = await getDocs(query(collection(db, 'usuarios'), where('dni', '==', dni)))
      if (snap.empty) { setErrorMessage('No se encontró cliente con ese DNI.'); return }
      const clienteDoc = snap.docs[0]
      const data = clienteDoc.data()
      const tier = getTierForPoints(data.puntosAcumulados ?? data.puntos, config)
      setClientInfo({
        id:           clienteDoc.id,
        name:         [data.nombre, data.apellido].filter(Boolean).join(' '),
        email:        data.email || '',
        totalPoints:  data.puntos ?? 0,
        tier:         tier.nombre,
        multiplicador: tier.multiplicador,
      })
    } catch {
      setErrorMessage('Error al buscar el cliente.')
    }
  }

  // ─── Acreditar puntos via Cloud Function ─────────────────────────────────────
  const handleAcreditarPuntos = async () => {
    if (isProcessing || !clientInfo || calculatedPoints === null) return
    setIsProcessing(true)
    resetMessages()
    try {
      const result = await acreditarPuntosFn({
        clienteId:       clientInfo.id,
        puntosAcreditar: calculatedPoints,
        montoVenta:      purchaseAmount,
        motivo:          `Acreditación por venta de $${purchaseAmount}`,
      })
      const { saldoPosterior, puntosAcreditados } = result.data
      setSuccessMessage(
        `¡Puntos registrados! +${puntosAcreditados} puntos. Nuevo saldo: ${Number(saldoPosterior).toLocaleString('es-AR')}`
      )
      setClientInfo({ ...clientInfo, totalPoints: saldoPosterior })
      setPurchaseAmount('')
      setCalculatedPoints(null)
      setDni('')
      setClientInfo(null)
    } catch (err) {
      setErrorMessage(mensajeError(err.message))
    } finally {
      setIsProcessing(false)
    }
  }

  // ─── Validar voucher via Cloud Function ──────────────────────────────────────
  const handleValidarVoucher = async () => {
    if (isProcessing || !voucherCode.trim()) {
      setErrorMessage('Ingresá el código del voucher.')
      return
    }
    setVoucherValidado(null)
    setConfirmarVoucher(false)
    resetMessages()
    setIsProcessing(true)
    try {
      const result = await validarVoucherFn({ code: voucherCode.trim() })
      const datos = result.data
      // Verificar que el voucher pertenece al cliente buscado si hay uno seleccionado
      if (clientInfo && datos.clienteUid && datos.clienteUid !== clientInfo.id) {
        setErrorMessage('Este voucher no pertenece al cliente seleccionado.')
        return
      }
      setVoucherValidado(datos)
      setConfirmarVoucher(true) // mostrar pantalla de confirmación con aporte
    } catch (err) {
      setErrorMessage(mensajeError(err.message))
    } finally {
      setIsProcessing(false)
    }
  }

  // ─── Utilizar voucher (operador confirma tras ver aporte en dinero) ──────────
  const handleUtilizarVoucher = async () => {
    if (isProcessing || !voucherValidado?.voucherId) return
    setIsProcessing(true)
    resetMessages()
    try {
      await utilizarVoucherFn({
        voucherId: voucherValidado.voucherId,
        localId:   localSeleccionado || operador?.local || '',
      })
      const aporte = Number(voucherValidado.aporteDinero || 0)
      setSuccessMessage(
        aporte > 0
          ? `Voucher utilizado. Cobrale $${money(aporte)} al cliente.`
          : `¡Voucher utilizado! Beneficio "${voucherValidado.premioNombre}" aplicado.`
      )
      setVoucherCode('')
      setVoucherValidado(null)
      setConfirmarVoucher(false)
    } catch (err) {
      setErrorMessage(mensajeError(err.message))
      setConfirmarVoucher(false)
    } finally {
      setIsProcessing(false)
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-maja-cream font-sans">
        <p className="text-maja-ink/50 font-semibold">Cargando caja...</p>
      </div>
    )
  }

  const localMostrado = localSeleccionado || operador?.local || ''
  const inputClass    = 'w-full h-12 px-4 rounded-2xl border border-black/10 focus:outline-none focus:ring-2 focus:ring-maja-red bg-white'

  return (
    <div className="min-h-screen bg-maja-cream font-sans">
      <header className="sticky top-0 z-40 bg-maja-ink text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo-oficial.png" alt="Maja Morena" className="h-11 w-11 object-contain" />
            <div className="min-w-0">
              <p className="font-display text-xl leading-none">Caja</p>
              <p className="text-xs text-white/55 truncate mt-1">
                {operador ? [operador.nombre, operador.apellido].filter(Boolean).join(' ') : 'Operador'}
                {localMostrado ? ` · ${localMostrado}` : ''}
              </p>
            </div>
          </div>
          <button onClick={onLogout} className="h-10 px-4 rounded-full bg-white/10 text-sm font-semibold hover:bg-white/20">
            Salir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-4">

        {/* PASO 01 — Buscar socio */}
        <section className="rounded-[1.6rem] border border-black/5 p-6 bg-white/80 shadow-soft">
          <p className="text-[11px] uppercase tracking-[0.22em] text-maja-ink/40">Paso 01</p>
          <h2 className="font-display text-3xl mt-1 mb-1">Buscar socio</h2>
          <p className="text-sm text-maja-ink/50 mb-4">Ingresá el DNI para acreditar puntos o validar un voucher.</p>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              placeholder="DNI del cliente"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchClient()}
              className={inputClass}
            />
            <button
              onClick={handleSearchClient}
              disabled={isProcessing}
              className="h-12 px-6 rounded-full bg-maja-red text-white font-semibold disabled:opacity-50"
            >
              Buscar
            </button>
          </div>
          {clientInfo && (
            <div className="mt-5 rounded-2xl bg-maja-cream p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-maja-ink/40">Socio</p>
                <p className="font-semibold text-lg tracking-tight">{clientInfo.name}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-maja-ink/40">Puntos</p>
                <p className="font-display text-2xl text-maja-red">{Number(clientInfo.totalPoints || 0).toLocaleString('es-AR')}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-maja-ink/40">Nivel</p>
                <p className="font-semibold text-lg">{clientInfo.tier}</p>
              </div>
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* PASO 02 — Acreditar compra */}
          <section className="rounded-[1.6rem] border border-black/5 p-6 bg-white/80 shadow-soft">
            <p className="text-[11px] uppercase tracking-[0.22em] text-maja-ink/40">Paso 02</p>
            <h2 className="font-display text-3xl mt-1 mb-1">Acreditar compra</h2>
            <p className="text-sm text-maja-ink/50 mb-4">Cargá el monto; los puntos se calculan solos.</p>
            {config && (
              <p className="text-sm font-extrabold text-maja-red mb-4">
                Tasa: 1 punto cada ${getPesosPorPunto(config).toLocaleString('es-AR')}
                {clientInfo ? ` · ${clientInfo.tier} x${clientInfo.multiplicador}` : ''}
              </p>
            )}
            <label className="block text-sm font-semibold text-maja-ink/70 mb-2">Monto de la venta ($)</label>
            <input
              type="number"
              step="0.01"
              placeholder="Ej: 15000"
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e.target.value)}
              disabled={!clientInfo || isProcessing}
              className={`${inputClass} ${!clientInfo || isProcessing ? 'opacity-50' : ''}`}
            />
            {calculatedPoints !== null && (
              <div className="mt-5 rounded-2xl bg-maja-ink text-white p-5">
                <p className="text-white/60 text-sm">Puntos a acreditar</p>
                <p className="font-display text-4xl mt-1">{calculatedPoints.toLocaleString('es-AR')}</p>
                {purchaseAmount && (
                  <p className="text-white/50 text-sm mt-2">
                    ${Number(purchaseAmount).toLocaleString('es-AR')} × {getPuntosPorPeso(config)} pts/$
                    {clientInfo?.multiplicador && clientInfo.multiplicador !== 1 ? ` × ${clientInfo.multiplicador}` : ''}
                  </p>
                )}
              </div>
            )}
            {calculatedPoints !== null && (
              <button
                onClick={handleAcreditarPuntos}
                disabled={isProcessing}
                className="mt-4 w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-50"
              >
                {isProcessing ? 'Acreditando...' : `Acreditar +${calculatedPoints} puntos`}
              </button>
            )}
          </section>

          {/* PASO 03 — Validar voucher */}
          <section className="rounded-[1.6rem] border border-black/5 p-6 bg-white/80 shadow-soft">
            <p className="text-[11px] uppercase tracking-[0.22em] text-maja-ink/40">Paso 03</p>
            <h2 className="font-display text-3xl mt-1 mb-1">Voucher</h2>
            <p className="text-sm text-maja-ink/50 mb-4">Ingresá el código del voucher del socio.</p>
            <input
              type="text"
              placeholder="Código del voucher"
              value={voucherCode}
              onChange={(e) => { setVoucherCode(e.target.value); setVoucherValidado(null); setConfirmarVoucher(false) }}
              onKeyDown={(e) => e.key === 'Enter' && handleValidarVoucher()}
              className={inputClass}
            />
            <button
              onClick={handleValidarVoucher}
              disabled={isProcessing || !voucherCode.trim()}
              className="mt-4 h-12 w-full rounded-full border border-black/10 font-semibold disabled:opacity-50 hover:border-maja-red"
            >
              Validar
            </button>

            {/* Confirmación de uso — muestra aporte en dinero ANTES de confirmar */}
            {confirmarVoucher && voucherValidado && (
              <div className="mt-5 rounded-2xl bg-maja-cream p-5 space-y-3">
                <p className="text-[11px] uppercase tracking-[0.18em] text-maja-ink/40">Voucher válido — confirmá antes de usar</p>
                <p className="font-semibold text-lg mt-1">{voucherValidado.premioNombre}</p>
                <p className="font-mono text-sm text-maja-ink/50">{voucherValidado.code}</p>
                <p className="text-sm text-maja-ink/60">{money(voucherValidado.puntosUtilizados)} pts utilizados</p>

                {Number(voucherValidado.aporteDinero) > 0 ? (
                  <div className="rounded-xl bg-maja-ink text-white p-4">
                    <p className="text-xs text-white/60 uppercase tracking-widest">Importe a cobrar al cliente</p>
                    <p className="font-display text-4xl text-white mt-1">
                      ${money(voucherValidado.aporteDinero)}
                    </p>
                    <p className="text-xs text-white/50 mt-1">Cobrá este importe ANTES de confirmar la utilización.</p>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-emerald-700">Sin aporte monetario — beneficio 100% puntos.</p>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setConfirmarVoucher(false); setVoucherValidado(null); setVoucherCode('') }}
                    disabled={isProcessing}
                    className="flex-1 h-11 rounded-full border border-black/10 font-semibold"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUtilizarVoucher}
                    disabled={isProcessing}
                    className="flex-1 h-11 rounded-full bg-maja-red text-white font-bold disabled:opacity-60"
                  >
                    {isProcessing ? 'Procesando...' : 'Confirmar uso'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        {errorMessage   && <p className="text-center font-semibold text-maja-red">{errorMessage}</p>}
        {successMessage && (
          <div className="fixed bottom-4 right-4 z-50">
            <div className="bg-maja-ink text-white px-6 py-4 rounded-2xl shadow-soft max-w-sm">
              <p className="font-semibold">Listo</p>
              <p className="text-sm text-white/80">{successMessage}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default OperatorDashboard
